import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSearch, faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api from '../../api/api';
import { Dialog, DialogBackdrop, Transition } from '@headlessui/react';
import _ from 'lodash';
import ComboBox from '../../components/atoms/ComboBox';
import Pagination from '../../components/Pagination';
import LoadingLogo from '../../components/Loading';
import { routes } from '../../config/utility';
import { Link } from 'react-router-dom';

const PetOwnerPet = ({ user, role }) => {
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editPet, setEditPet] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isShowModalOpen, setIsShowModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [meta, setMeta] = useState({
    total: 0,
    per_page: 10,
    current_page: 1,
    last_page: 1,
    from: 1,
    to: 1,
  });

  const [characteristics, setCharacteristics] = useState({
    pet_types: [],
    pet_breeds: [],
    pet_allergies: [],
  });

  // Debounced function to handle search input (prevents excessive API calls)
  const debouncedFetchPets = useCallback(
    _.debounce(() => {
      fetchPets();
    }, 500), // Delay of 500ms
    [filter, currentPage]
  );

  const debouncedFetchCharacteristics = useCallback(
    _.debounce(() => {
      fetchCharacteristics();
    }, 500), // Delay of 500ms
    []
  );

  useEffect(() => {
    // Call the debounced fetchPets function
    debouncedFetchPets();
  }, [filter, currentPage, debouncedFetchPets]);

  // Fetch pets function
  const fetchPets = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/pets`, {
        params: {
          search: searchTerm,
          pet_type: filter,
          page: currentPage,
        },
      });
      setPets(response.data);
      setMeta(response.meta);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to fetch pets.');
      setLoading(false);
    }
  };

  // Fetch characteristics (pet types, breeds, allergies)
  const fetchCharacteristics = async (petTypeId = null) => {
    try {
      const response = await api.get('/pets/characteristics', {
        params: {
          pet_type_id: petTypeId || undefined,
        },
      });
      setCharacteristics({
        pet_types: response.data.pet_types,
        pet_breeds: response.data.pet_breeds,
        pet_allergies: response.data.pet_allergies,
      });
    } catch (error) {
      toast.error('Failed to load characteristics.');
    }
  };

  useEffect(() => {
    debouncedFetchCharacteristics(); // Fetch pet characteristics on initial load
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this pet?')) {
      try {
        // await api.delete(`/pets/${id}`);
        toast.success('Pet deleted successfully.');
        fetchPets();
      } catch (error) {
        toast.error('Failed to delete pet.');
      }
    }
  };

  const handleEdit = (pet) => {
    setEditPet(pet);
    setIsModalOpen(true);
    fetchCharacteristics(pet.pet_type_id); // Fetch related breeds/allergies
  };

  const handleShow = (pet) => {
    setEditPet(pet);
    setIsShowModalOpen(true);
  };

  const handleAdd = () => {
    setEditPet(null);
    setIsModalOpen(true);
    fetchCharacteristics(); // Load characteristics without filtering by type
  };

  const handleSave = () => {
    setIsModalOpen(false);
    fetchPets();
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handleShowCancel = () => {
    setIsShowModalOpen(false);
  };

  const handleNextPage = () => {
    if (meta.current_page < meta.last_page) {
      setCurrentPage(meta.current_page + 1);
    }
  };

  const handlePrevPage = () => {
    if (meta.current_page > 1) {
      setCurrentPage(meta.current_page - 1);
    }
  };

  const renderPageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= meta.last_page; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`px-3 py-1 rounded-lg border border-tblue ${i === meta.current_page ? 'bg-tblue text-white' : 'bg-white text-gray-700'
            } hover:bg-tblue hover:text-white mx-1`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  if (loading) {
    return <LoadingLogo />;
  }

  return (
    <div className="mx-auto  ">
      {/* Header and Actions */}
      <div className="flex justify-between items-center mb-4 px-4 sm:px-0">

        <div className="flex items-center gap-3">
          <h1 className="text-primary text-2xl sm:text-3xl font-bold">Pets</h1>
          <Link
            to={`${routes.guide}?type=pet`}
            className="group flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-purple-100 hover:bg-purple-200 transition-all duration-300"
            title="View Walk Guide"
          >
            <FontAwesomeIcon
              icon={faQuestionCircle}
              className="text-primary text-lg sm:text-xl group-hover:scale-110 transition-transform duration-300"
            />
          </Link>
        </div>
        <button
          onClick={handleAdd}
          className="bg-primary text-white px-4 py-2 rounded-lg flex items-center hover:bg-accent"
        >
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          Add New Pet
        </button>
      </div>

      {/* Search and Filter */}
      <div className="flex space-x-4 mb-6 px-4 sm:px-0">
        <div className="flex items-center border border-gray-300 rounded-lg p-2 shadow-md transition-all duration-300 hover:shadow-lg focus-within:shadow-2xl focus-within:bg-bodyMain">
          <FontAwesomeIcon
            icon={faSearch}
            className="text-gray-400 mr-2 transition-transform duration-300 hover:scale-110"
          />
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                fetchPets();
              }
            }}
            className="outline-none focus:ring-0 bg-transparent  w-full text-gray-700 placeholder-gray-400 focus:placeholder-gray-500 focus:text-black"
          />
        </div>

        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="border rounded-lg p-2 shadow-sm"
        >
          <option value="all">All Types</option>
          {characteristics.pet_types.map((type) => (
            <option value={type.id}>{type.name}</option>
          ))}

        </select>
      </div>


      {/* Pets Card */}
      {/* Desktop/Web Grid */}
      <div className="hidden sm:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
        {pets.map((pet) => (
          <div
            key={pet.id}
            className="relative bg-white shadow-xl rounded-3xl overflow-hidden transform transition-transform hover:-translate-y-3 hover:shadow-2xl duration-300"
          >
            {/* Fun Curved Top Bar */}
            <div className="absolute top-0 left-0 w-full h-3 bg-gradient-to-r from-secondary to-accent rounded-b-full"></div>

            {/* Pet Image */}
            <div className="overflow-hidden rounded-t-3xl">
              <img
                src={pet.primary_image_url}
                alt={pet.name}
                className="w-full h-52 object-cover transition-transform duration-500 hover:scale-110"
              />
            </div>

            {/* Pet Information */}
            <div className="p-6 text-center">
              <h3 className="text-tblue text-3xl font-bold mb-2">{pet.name}</h3>
              <div className="flex justify-center space-x-4 mt-4">
                <button
                  onClick={() => handleShow(pet)}
                  className="px-5 py-2 bg-gradient-to-r from-tblue to-accent text-white rounded-full shadow-md hover:from-accent hover:to-tblue hover:shadow-lg transform hover:scale-105 transition-all"
                  title="Show Pet"
                >
                  🐾 Details
                </button>
                <button
                  onClick={() => handleEdit(pet)}
                  className="px-4 py-2 text-white bg-blue-500 rounded-full hover:bg-blue-700 transition-all transform hover:scale-105"
                  title="Edit Pet"
                >
                  ✏️ Edit
                </button>
              </div>
              {/* <button
                onClick={() => handleDelete(pet.id)}
                className="mt-4 px-4 py-2 bg-red-500 text-white rounded-full hover:bg-red-700 transition-transform transform hover:scale-105"
                title="Delete Pet"
              >
                ❌ Delete
              </button> */}
            </div>
          </div>
        ))}
      </div>

      {/* Mobile App-like Carousel */}
      <div className="sm:hidden">
        <div className="flex overflow-x-auto space-x-5 pb-6 -mx-3 px-3 snap-x snap-mandatory">
          {pets.map((pet) => (
            <div
              key={pet.id}
              className="min-w-[88vw] max-w-xs bg-white/60 backdrop-blur-md border border-white/30 rounded-3xl overflow-hidden flex-shrink-0 snap-center relative"

            >
              {/* Top Bar */}
              <div className="w-full h-2 bg-gradient-to-r from-secondary to-accent rounded-b-full"></div>
              {/* Pet Image */}
              <div className="overflow-hidden rounded-t-3xl">
                <img
                  src={pet.primary_image_url}
                  alt={pet.name}
                  className="w-full h-48 object-cover"
                />
              </div>
              {/* Info */}
              <div className="p-4 pt-3 text-center">
                <h3 className="text-tblue text-2xl font-extrabold mb-1 tracking-tight">{pet.name}</h3>
                <p className="text-gray-500 text-sm mb-2">{pet.type?.name} • {pet.breed?.name}</p>
                <div className="flex justify-center gap-2 mb-3">
                  <span className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full shadow">{pet.age} yrs</span>
                  <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full shadow">{pet.weight} kg</span>
                </div>
                {/* Floating Action Buttons */}
                <div className="flex justify-center gap-4 mt-4">
                  <button
                    onClick={() => handleShow(pet)}
                    className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-tblue to-accent text-white rounded-full shadow-lg hover:scale-110 transition-transform text-xl"
                    title="Show Pet"
                  >
                    <span role="img" aria-label="Details">🐾</span>
                  </button>
                  <button
                    onClick={() => handleEdit(pet)}
                    className="flex items-center justify-center w-12 h-12 bg-blue-500 text-white rounded-full shadow-lg hover:bg-blue-700 hover:scale-110 transition-transform text-xl"
                    title="Edit Pet"
                  >
                    <span role="img" aria-label="Edit">✏️</span>
                  </button>
                  {/* <button
                    onClick={() => handleDelete(pet.id)}
                    className="flex items-center justify-center w-12 h-12 bg-red-500 text-white rounded-full shadow-lg hover:bg-red-700 hover:scale-110 transition-transform text-xl"
                    title="Delete Pet"
                  >
                    <span role="img" aria-label="Delete">❌</span>
                  </button> */}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>



      {/* Smart Pagination */}
      <Pagination
        meta={meta}
        onPageChange={(page) => setCurrentPage(page)}
      />




      {/* CRUD Modal for Adding/Editing Pets */}
      {isModalOpen && (
        <CRUModal
          item={editPet}
          characteristics={characteristics}
          onSave={handleSave}
          onCancel={handleCancel}
          fetchCharacteristics={fetchCharacteristics}
        />
      )}

      {isShowModalOpen && (
        <ShowModal
          item={editPet}
          onCancel={handleShowCancel}
        />
      )}
    </div>
  );
};

export default PetOwnerPet;



export const CRUModal = ({ item, characteristics, fetchCharacteristics, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    weight: '',
    pet_type_id: '',
    pet_breed_id: '',
    owner_id: '',
    gender: 'unknown',
    microchip_number: '',
    is_active: true,
    selectedAllergies: [],
    images: [],
    removedImages: [],
    vaccine_certificate: null,
    removedVaccineCertificate: false,
  });

  const [selectedPetType, setSelectedPetType] = useState(null);
  const [selectedPetBreed, setSelectedPetBreed] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]); // To track newly selected images
  const [previewImages, setPreviewImages] = useState([]); // Preview for new images
  const [vaccinePreview, setVaccinePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);


  // Only fetch characteristics when the item changes, and ensure it's not called repeatedly
  useEffect(() => {
    if (item) {
      // Populate form data
      setFormData({
        _method: 'put',
        name: item.name || '',
        age: item.age || '',
        weight: item.weight || '',
        pet_type_id: item.type?.id || '',
        pet_type_name: item.type?.name || '',
        pet_breed_id: item.breed?.id || '',
        pet_breed_name: item.breed?.name || '',
        gender: item.gender || 'unknown',
        microchip_number: item.microchip_number || '',
        selectedAllergies: item.allergies ? item.allergies.map((allergy) => allergy.id) : [],
        images: item.images || [],
        removedImages: [],
        vaccine_certificate: item.vaccine_certificate || null,
        removedVaccineCertificate: false,
      });

      // Set the selected pet type and breed from the response data
      // const initialType = characteristics.pet_types.find((type) => type.id === item.type?.id);
      // const initialBreed = characteristics.pet_breeds.find((breed) => breed.id === item.breed?.id) ;

      // setSelectedPetType(initialType);
      // setSelectedPetBreed(initialBreed);

      // Only fetch characteristics once when the item type exists
      if (item.type) {
        fetchCharacteristics(item.type.id);
      }
      if (item.vaccine_certificate) {
        setVaccinePreview(item.vaccine_certificate);
      }
    }
  }, []);


  const handleVaccineCertificateChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prevData) => ({
        ...prevData,
        vaccine_certificate: file,
        removedVaccineCertificate: false,
      }));
      setVaccinePreview(URL.createObjectURL(file));
    }
  };

  const handleRemoveVaccineCertificate = () => {
    setFormData({ ...formData, vaccine_certificate: null, removedVaccineCertificate: true });
    setVaccinePreview(null);
  };

  // Handle selecting new images
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles((prev) => [...prev, ...files]);

    // Preview the newly selected images
    const previews = files.map((file) => URL.createObjectURL(file));
    setPreviewImages((prev) => [...prev, ...previews]);
  };

  // Remove existing image (edit mode)
  const handleRemoveExistingImage = (imageId) => {
    setFormData({
      ...formData,
      removedImages: [...formData.removedImages, imageId], // Track removed images
      images: formData.images.filter((img) => img.id !== imageId) // Remove from UI
    });
  };

  // Remove newly added (preview) images
  const handleRemoveNewImage = (index) => {
    setPreviewImages(previewImages.filter((_, i) => i !== index));
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
  };

  // Synchronize formData when type, breed, or owner changes
  const handleTypeChange = (value) => {
    setSelectedPetType(value);
    setFormData({ ...formData, pet_type_id: value?.id || '' });
    fetchCharacteristics(value?.id); // Fetch breeds/allergies for selected type
  };

  const handleBreedChange = (value) => {
    setSelectedPetBreed(value);
    setFormData({ ...formData, pet_breed_id: value?.id || '' });
  };


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAllergyChange = (allergyId) => {
    setFormData((prevData) => {
      const isSelected = prevData.selectedAllergies.includes(allergyId);
      const updatedAllergies = isSelected
        ? prevData.selectedAllergies.filter((id) => id !== allergyId)
        : [...prevData.selectedAllergies, allergyId];
      return { ...prevData, selectedAllergies: updatedAllergies };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return; // Prevent multiple submissions

    try {
      setIsSubmitting(true); // Start loading
      const { selectedAllergies, removedImages, removedVaccineCertificate, vaccine_certificate, ...petData } = formData;
      // ... rest of your existing submit logic ...

      const formDataToSend = new FormData();
      Object.keys(petData).forEach((key) => {
        formDataToSend.append(key, petData[key]);
      });

      // Handle allergies - explicitly send empty array if no allergies selected
      if (selectedAllergies.length === 0) {
        formDataToSend.append('clear_allergies', true); // Flag to indicate allergies should be cleared
      } else {
        selectedAllergies.forEach((id) => {
          formDataToSend.append('allergies[]', id);
        });
      }

      if (selectedFiles.length === 0) {
        formDataToSend.append('images', []);
      } else {
        selectedFiles.forEach((file) => {
          formDataToSend.append('images[]', file);
        });
      }

      removedImages.forEach((imageId) => {
        formDataToSend.append('removed_images[]', imageId); // Attach removed image IDs
      });

      if (vaccine_certificate instanceof File) {
        formDataToSend.append('vaccine_certificate', vaccine_certificate);
      }

      if (removedVaccineCertificate) {
        formDataToSend.append('removed_vaccine_certificate', true);
      }

      if (item) {
        await api.post(`/pets/${item.id}`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Pet updated successfully');
      } else {
        await api.post('/pets', formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Pet created successfully');
      }
      onSave();
    } catch (error) {
      // toast.error('Error saving pet. Please try again.');
    } finally {
      setIsSubmitting(false); // End loading
    }
  };

  // Filter function for pet types, breeds, and owners based on query input
  // const filterPetTypes = (query) => {
  //   return characteristics.pet_types.filter((type) =>
  //     type.name.toLowerCase().includes(query.toLowerCase())
  //   );
  // };

  // const filterPetBreeds = (query) => {
  //   return characteristics.pet_breeds.filter((breed) =>
  //     breed.name.toLowerCase().includes(query.toLowerCase())
  //   );
  // };


  return (
    <Transition appear show={true} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onCancel}>
        <DialogBackdrop className="fixed inset-0 bg-black/30 backdrop-blur-sm" />

        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-7xl transform overflow-hidden rounded-2xl bg-white  text-left align-middle shadow-xl transition-all">

                <div className="bg-tblue text-white p-4">
                  <h1 className="text-2xl font-bold"> {item ? 'Edit Pet' : 'Add New Pet'}</h1>
                </div>

                <div className="mt-4 p-6">
                  <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                      {/* Pet Name */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name || ''}
                          onChange={handleChange}
                          required
                          className="block w-full p-2 border rounded-lg"
                        />
                      </div>

                      {/* Age */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Age (Years) <span className="text-red-500">*</span></label>
                        <input
                          type="number"
                          name="age"
                          required
                          value={formData.age || ''}
                          onChange={handleChange}
                          className="block w-full p-2 border rounded-lg"
                        />
                      </div>

                      {/* Weight */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Weight (KG) </label>
                        <input
                          type="number"
                          name="weight"
                          step="0.01"
                          value={formData.weight || ''}
                          onChange={handleChange}
                          className="block w-full p-2 border rounded-lg"
                        />
                      </div>

                      {/* Pet Type ComboBox */}
                      <div className="mb-4">
                        {
                          !item ?
                            <ComboBox
                              label="Type"
                              options={characteristics.pet_types}
                              value={selectedPetType}
                              onChange={handleTypeChange}
                              disabled={!!item}
                              required={true}
                            /> :
                            <>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Type </label>
                              <input
                                type="text"
                                value={formData.pet_type_name}
                                className="block w-full p-2 border rounded-lg  text-gray-700 cursor-not-allowed 
                              hover:bg-gray-200 transition-all duration-300"
                                disabled
                              />
                            </>
                        }
                      </div>

                      {/* Pet Breed ComboBox */}
                      <div className="mb-4">

                        {
                          !item ?
                            <ComboBox
                              label="Breed"
                              options={characteristics.pet_breeds}
                              value={selectedPetBreed}
                              onChange={handleBreedChange}
                              disabled={!!item}
                              required={true}
                            />
                            :
                            <>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Breed</label>
                              <input
                                type="text"
                                value={formData.pet_breed_name}
                                className="block w-full p-2 border rounded-lg  text-gray-700 cursor-not-allowed 
                              hover:bg-gray-200 transition-all duration-300"
                                disabled
                              />
                            </>
                        }

                      </div>

                      {/* Gender */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Gender <span className="text-red-500">*</span></label>
                        <select
                          name="gender"
                          value={formData.gender || 'unknown'}
                          onChange={handleChange}
                          className="w-full p-2 border rounded-lg"
                        >
                          <option value="unknown">Unknown</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                        </select>
                      </div>

                      {/* Vaccination Date */}
                      {/* <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Vaccinated At</label>
                        <input
                          type="date"
                          name="vaccinated_at"
                          value={formData.vaccinated_at || ''}
                          onChange={handleChange}
                          className="block w-full p-2 border rounded-lg"
                        />
                      </div> */}

                      {/* Microchip Number */}
                      {/* <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Microchip Number</label>
                        <input
                          type="text"
                          name="microchip_number"
                          value={formData.microchip_number || ''}
                          onChange={handleChange}
                          className="block w-full p-2 border rounded-lg"
                        />
                      </div> */}
                    </div>

                    {/* Allergies */}
                    <div className="mb-4">
                      <label className="block text-lg font-medium text-tblue mb-1">Allergies</label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {characteristics.pet_allergies.map((allergy) => (
                          <div
                            key={allergy.id}
                            onClick={() => handleAllergyChange(allergy.id)}
                            className="flex items-center p-2 bg-white border border-gray-300 rounded-lg hover:shadow-md transition-shadow"
                          >
                            <input
                              type="checkbox"
                              id={`allergy-${allergy.id}`}
                              checked={formData.selectedAllergies.includes(allergy.id)}
                              onChange={() => handleAllergyChange(allergy.id)}
                              className="form-checkbox h-5 w-5 text-accent rounded focus:ring-accent focus:ring-2"
                            />
                            <label
                              htmlFor={`allergy-${allergy.id}`}
                              className={`ml-3 ${formData.selectedAllergies.includes(allergy.id) ? 'text-accent' : 'text-body'}  cursor-pointer hover:text-accent transition-colors`}
                            >
                              {allergy.name}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>


                    {/* Image Upload Section */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Upload Images</label>

                      <div className="flex items-center space-x-4 mt-2">

                        {/* Show existing images (edit mode) */}
                        {formData.images.length > 0 && (
                          <div className="flex space-x-4">
                            {formData.images.map((img) => (
                              <div key={img.id} className="relative group">
                                <img
                                  src={img.url} // Assuming 'url' field in image object
                                  alt="Pet image"
                                  className="w-24 h-24 object-cover rounded-lg shadow-md transform transition duration-300 ease-in-out hover:scale-105"
                                />
                                <button
                                  type="button"
                                  className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-in-out"
                                  onClick={() => handleRemoveExistingImage(img.id)}
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Show preview of newly added images */}
                        {previewImages.length > 0 && (
                          <div className="flex space-x-4">
                            {previewImages.map((img, index) => (
                              <div key={index} className="relative group">
                                <img
                                  src={img}
                                  alt="New upload"
                                  className="w-24 h-24 object-cover rounded-lg shadow-md transform transition duration-300 ease-in-out hover:scale-105"
                                />
                                <button
                                  type="button"
                                  className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-in-out"
                                  onClick={() => handleRemoveNewImage(index)}
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Upload Icon for Adding New Images */}
                        <div className="relative group">
                          <button
                            type="button"
                            className="w-24 h-24 flex flex-col items-center justify-center border-2 border-purple-500 rounded-lg hover:bg-purple-50 transition-colors duration-300 ease-in-out"
                            onClick={() => document.getElementById('file-upload').click()} // Trigger file input click
                          >
                            <div className="flex items-center justify-center w-10 h-10 bg-purple-500 text-white rounded-full">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                              </svg>
                            </div>
                            <span className="mt-2 text-sm text-gray-500">Add More</span>
                          </button>

                          {/* Hidden File Input */}
                          <input
                            id="file-upload"
                            type="file"
                            multiple
                            onChange={handleFileChange} // Your existing handler for file changes
                            className="hidden"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Vaccine Certificate Upload */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Vaccine Certificate</label>
                      <div className="flex items-center space-x-4 mt-2">
                        {vaccinePreview && (
                          <div className="relative group">
                            <img
                              src={vaccinePreview}
                              alt="Vaccine Certificate"
                              className="w-24 h-24 object-cover rounded-lg shadow-md transform transition duration-300 hover:scale-105"
                            />
                            <button
                              type="button"
                              className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100"
                              onClick={handleRemoveVaccineCertificate}
                            >
                              ✕
                            </button>
                          </div>
                        )}
                        {!vaccinePreview && (
                          <div className="relative group">
                            <button
                              type="button"
                              className="w-24 h-24 flex flex-col items-center justify-center border-2 border-purple-500 rounded-lg hover:bg-purple-50 transition-colors"
                              onClick={() => document.getElementById('vaccine-upload').click()}
                            >
                              <span className="text-gray-500">Upload</span>
                            </button>
                            <input
                              id="vaccine-upload"
                              type="file"
                              accept="image/*"
                              onChange={handleVaccineCertificateChange}
                              className="hidden"
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end mt-6">
                      <button
                        type="button"
                        onClick={onCancel}
                        className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg mr-2"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`px-4 text-white py-2 rounded-lg flex items-center justify-center transition-all duration-200 ${isSubmitting
                          ? 'bg-primary/70 cursor-not-allowed'
                          : 'bg-primary hover:bg-primary/90'
                          }`}
                      >
                        {isSubmitting ? (
                          <>
                            <svg
                              className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              />
                            </svg>
                            {item ? 'Updating...' : 'Saving...'}
                          </>
                        ) : (
                          item ? 'Update' : 'Save'
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};


export const ShowModal = ({ item, onCancel }) => {
  const [primaryImage, setPrimaryImage] = useState(item.primary_image_url); // Track the primary image

  // Function to handle the image click and update the primary image
  const handleImageClick = (imageUrl) => {
    setPrimaryImage(imageUrl);
  };

  return (
    <Transition appear show={true} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onCancel}>
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-7xl transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all">

                {/* Header */}
                <div className="bg-tblue text-white p-4">
                  <h1 className="text-2xl font-bold">Pet Profile</h1>
                </div>

                {/* Content */}
                <div className="p-6 lg:flex gap-6">
                  {/* Left Section: Pet Image */}
                  <div className="flex-1">
                    <div className="relative">
                      <img
                        src={primaryImage} // Display the primary image here
                        alt="Pet"
                        className="w-full h-64 object-cover rounded-lg"
                      />
                    </div>

                    {/* Pet More Photos */}
                    <h3 className="text-tblue text-lg font-semibold mt-4">Pet More Photos</h3>
                    <div className="flex gap-4 mt-2">
                      {item.images.map((img) => (
                        <div key={img.url} className="flex flex-col items-center">
                          <img
                            src={img.url}
                            alt="Pet"
                            onClick={() => handleImageClick(img.url)} // Change primary image on click
                            className="w-16 h-16 object-cover rounded-lg cursor-pointer"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Right Section: Pet Details */}
                  <div className="flex-1 mt-6 lg:mt-0 lg:pl-6">

                    {/* Pet Info */}
                    <div className="grid grid-cols-2 gap-4 mt-6">
                      <div>
                        <h4 className="text-tblue font-semibold">Name</h4>
                        <p className="text-body">{item.name}</p>
                      </div>
                      <div>
                        <h4 className="text-tblue font-semibold">Species</h4>
                        <p className="text-body">{item.type.name}</p>
                      </div>
                      <div>
                        <h4 className="text-tblue font-semibold">Gender</h4>
                        <p className="text-body">{item.gender}</p>
                      </div>
                      <div>
                        <h4 className="text-tblue font-semibold">Breed</h4>
                        <p className="text-body">{item.breed.name}</p>
                      </div>
                      <div>
                        <h4 className="text-tblue font-semibold">Age</h4>
                        <p className="text-body">{item.age} years</p>
                      </div>
                      <div>
                        <h4 className="text-tblue font-semibold">Weight</h4>
                        <p className="text-body">{item.weight} KG</p>
                      </div>
                      <div>
                        <h4 className="text-tblue font-semibold">Vaccinated</h4>
                        <p className="text-body">{item.is_vaccinated ? 'Yes' : 'No'}</p>
                      </div>
                      {item.is_vaccinated && (
                        <div>
                          <h4 className="text-tblue font-semibold">Vaccinated At</h4>
                          <p className="text-body">{item.vaccinated_at}</p>
                        </div>
                      )}
                      <div>
                        <h4 className="text-tblue font-semibold mb-1">Allergies</h4>
                        {item.allergies.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {item.allergies.map((allergy) => (
                              <span
                                key={allergy.id}
                                className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-1 rounded-full"
                              >
                                {allergy.name}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-500">No Allergies</span>
                        )}
                      </div>

                    </div>
                  </div>
                </div>

              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};










// {pets.map((pet) => (
//   <div
//     key={pet.id}
//     className="relative bg-gray-900 text-white shadow-lg rounded-xl overflow-hidden transition-transform transform hover:-translate-y-2 hover:shadow-2xl duration-300 h-80"
//   >
//     <img
//       src={pet.primary_image_url}
//       alt={pet.name}
//       className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 hover:scale-105"
//     />

//     {/* Overlay Gradient for Text Visibility */}
//     <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent"></div>

//     {/* Pet Info and Actions (Overlayed) */}
//     <div className="relative z-10 p-6 flex flex-col justify-between h-full">
//       {/* Pet Name */}
//       <div>
//         <h3 className="text-3xl  text-accent font-bold mb-2">{pet.name}</h3>
//         <p className="text-lg text-white">{pet.breed.name}</p>
//         <p className="text-md text-white ">{pet.age} years</p>
//       </div>

//       {/* Action Buttons */}
//       <div className="flex justify-between items-center mt-4">
//         {/* Details Button */}
//         <button
//           onClick={() => handleShow(pet)}
//           className="px-6 py-2 bg-gradient-to-r from-accent to-tblue text-white rounded-full shadow-md hover:bg-gradient-to-l hover:shadow-lg transition-all duration-200"
//           title="Show Pet"
//         >
//           View Details
//         </button>

//         {/* Edit and Delete Icons */}
//         <div className="flex space-x-3">
//           {/* Edit Button */}
//           <button
//             onClick={() => handleEdit(pet)}
//             className="text-white hover:text-accent transition duration-200"
//             title="Edit Pet"
//           >
//             <FontAwesomeIcon
//               icon={faEdit}
//               className="text-xl cursor-pointer hover:text-accent transition-colors duration-200"
//             />
//           </button>

//           {/* Delete Button */}
//           <button
//             onClick={() => handleDelete(pet.id)}
//             className="text-white hover:text-red-600 transition duration-200"
//             title="Delete Pet"
//           >
//             <FontAwesomeIcon
//               icon={faTrashAlt}
//               className="text-xl cursor-pointer hover:text-red-600 transition-colors duration-200"
//             />
//           </button>
//         </div>
//       </div>
//     </div>
//   </div>
// ))}